// Client-side table export for report-history style pages: the page already
// holds every row in memory, so exporting is pure serialization — no backend.
//
// Safety notes baked in here:
// - CSV formula injection: a cell starting with = + - @ (or tab/CR variants)
//   executes when the CSV is opened in Excel. User-entered values (supplier,
//   owner) flow into these files, so such cells are prefixed with a quote.
//   XLSX is written with string cells only, which Excel never evaluates.
// - CSV gets a UTF-8 BOM so Excel renders units like µ and ₂ correctly.
import * as XLSX from "xlsx";

// [[header...], [cell...]...] — mirrors the on-screen table: rows render
// positionally via Object.entries against the header array, so export does too.
export function buildAoa(headers, rows, formatCell) {
  const head = (Array.isArray(headers) ? headers : []).map((h) => String(h ?? ""));
  const body = (Array.isArray(rows) ? rows : []).map((row) =>
    Object.entries(row || {}).map(([key, cell]) => {
      const v = formatCell ? formatCell(key, cell) : cell;
      return v == null ? "" : String(v);
    })
  );
  return [head, ...body];
}

const FORMULA_PREFIX = /^[=+\-@\t\r]/;

export function csvEscape(value) {
  let s = value == null ? "" : String(value);
  if (FORMULA_PREFIX.test(s)) s = "'" + s;
  if (/[",\n\r]/.test(s)) s = '"' + s.replace(/"/g, '""') + '"';
  return s;
}

export function toCsvString(aoa) {
  // BOM so Excel detects UTF-8 instead of the locale codepage.
  return "﻿" + aoa.map((row) => row.map(csvEscape).join(",")).join("\r\n");
}

// Filename part from user-visible values (site location, metric): keep it
// readable, drop anything a filesystem or browser could choke on.
export function sanitizeFilePart(s) {
  return (
    String(s ?? "")
      .trim()
      .replace(/[^\w.()-]+/g, "-")
      .replace(/-{2,}/g, "-")
      .replace(/^-|-$/g, "") || "export"
  );
}

export function exportFilename(parts, ext) {
  const stamp = new Date().toISOString().slice(0, 10);
  return [...parts.map(sanitizeFilePart).filter(Boolean), stamp].join("_") + "." + ext;
}

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function downloadAoaAsXlsx(aoa, filename, sheetName = "Report history") {
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  XLSX.writeFile(wb, filename);
}

export function downloadAoaAsCsv(aoa, filename) {
  triggerDownload(new Blob([toCsvString(aoa)], { type: "text/csv;charset=utf-8" }), filename);
}
